import { useState } from "react";
import { Search, Loader2, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const statusStyles = {
  New: { bg: "#EFF6F5", text: "#194449" },
  "In Progress": { bg: "#FDF6E3", text: "#8A6D1E" },
  Reviewed: { bg: "#F0F9EC", text: "#3F7D20" },
  Closed: { bg: "#F3F4F6", text: "#4B5563" },
};

export async function fetchRequestStatus(refId, email) {
  const res = await fetch(`${API}/api/request/status/${encodeURIComponent(refId)}?email=${encodeURIComponent(email)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Could not find that request.");
  return data;
}

export function StatusResultCard({ result, onRefresh, refreshing }) {
  const style = statusStyles[result.status] || statusStyles.New;
  return (
    <div className="rounded-2xl border p-5 text-left" style={{ borderColor: "#E2EBE9" }} data-testid="request-status-result">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: style.bg, color: style.text }} data-testid="request-status-badge">
          {result.status}
        </span>
        {onRefresh && (
          <button onClick={onRefresh} disabled={refreshing} data-testid="request-status-refresh-button" className="text-xs font-medium flex items-center gap-1 disabled:opacity-50" style={{ color: "#194449" }}>
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        )}
      </div>
      {result.review_comments && (
        <p className="text-sm text-gray-600 mb-2"><span className="font-semibold text-gray-800">Reviewer comments:</span> {result.review_comments}</p>
      )}
      <p className="text-xs text-gray-400">
        {"Submitted " + new Date(result.submitted_at).toLocaleString() + " \u00b7 Updated " + new Date(result.updated_at).toLocaleString()}
      </p>
    </div>
  );
}

export default function StatusCheckForm() {
  const [email, setEmail] = useState("");
  const [refId, setRefId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const handleCheck = async () => {
    setError("");
    setResult(null);
    if (!email.trim() || !refId.trim()) {
      setError("Enter both your email and reference ID.");
      return;
    }
    setLoading(true);
    try {
      const data = await fetchRequestStatus(refId.trim(), email.trim());
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border p-5" style={{ borderColor: "#E2EBE9", backgroundColor: "#F8FFFE" }} data-testid="request-status-check-form">
      <p className="text-sm font-semibold text-gray-800 mb-3">Check the status of a previous request</p>
      <div className="space-y-2.5 mb-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email used to submit"
          data-testid="request-status-email-input"
          className="w-full px-3.5 py-2.5 rounded-xl border text-sm bg-white focus:outline-none focus:ring-2"
          style={{ borderColor: "#DCE3E1", "--tw-ring-color": "#1D5C56" }}
        />
        <input
          type="text"
          value={refId}
          onChange={(e) => setRefId(e.target.value)}
          placeholder="Reference ID (from your confirmation)"
          data-testid="request-status-refid-input"
          className="w-full px-3.5 py-2.5 rounded-xl border text-sm bg-white focus:outline-none focus:ring-2"
          style={{ borderColor: "#DCE3E1", "--tw-ring-color": "#1D5C56" }}
        />
      </div>
      {error && <p className="text-sm mb-3" style={{ color: "#B5453D" }} data-testid="request-status-error">{error}</p>}
      <button
        onClick={handleCheck}
        disabled={loading}
        data-testid="request-status-check-button"
        className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white py-2.5 rounded-xl disabled:opacity-60"
        style={{ backgroundColor: "#194449" }}
        onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#0B2E38"; }}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#194449")}
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
        {loading ? "Checking\u2026" : "Check Status"}
      </button>
      {result && <div className="mt-4">
        <StatusResultCard result={result} />
      </div>}
    </div>
  );
}
